import {
  faCog,
  faLayerPlus,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

import { UpsertAgentModal } from "src/agents/UpsertAgentModal";
import { useIsPrebuiltReadOnly } from "src/agents/usePrebuiltReadOnly";
import { useAgent } from "src/api/agents/management";
import { Skeleton } from "src/design-system/Skeleton";
import { DashboardContent } from "src/layout/DashboardContent";
import { NavItem, NavSidebar } from "src/layout/NavSideBar";
import { PageHeader } from "src/layout/PageHeader";
import { appendCurrentFlags } from "src/router/featureFlags";
import { HashKeys } from "src/router/SearchParams";
import {
  AgentVersionsPageParams,
  getUrlToAgentHistoryPage,
  getUrlToAgentsPage,
  getUrlToAgentVersionsPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const AgentPage: React.FC = () => {
  const context = useOutletContext();
  const { orgId, wsId, agentId } = useParamsDecode<AgentVersionsPageParams>();
  const { data: agent, isLoading } = useAgent(agentId);
  const isReadOnly = useIsPrebuiltReadOnly(agent);
  const agentsPageUrl = getUrlToAgentsPage({ orgId, wsId });
  const location = useLocation();
  const navigate = useNavigate();

  const isEditModalOpen = !isReadOnly && location.hash === HashKeys.Edit;

  const handleCloseModal = () => {
    navigate(appendCurrentFlags(location.pathname));
  };

  return (
    <>
      <DashboardContent
        Header={
          <PageHeader
            breadcrumbs={[
              {
                key: "agents-page",
                breadcrumb: (
                  <PageHeader.Breadcrumb
                    dataLoc="agents-page"
                    href={agentsPageUrl}
                    label="Agents"
                    hideIcon
                  />
                ),
              },
              {
                key: "agent-versions",
                breadcrumb: isLoading ? (
                  <Skeleton height="h-4" width="w-24" />
                ) : (
                  <PageHeader.Breadcrumb
                    dataLoc="agent-versions"
                    href={agentsPageUrl}
                    label={agent?.name ?? "Unable to load Agent"}
                    active
                  />
                ),
              },
            ]}
          />
        }
        sidebar={<AgentNavSidebar isReadOnly={isReadOnly} />}
        removePadding
      >
        <Outlet context={context} />
      </DashboardContent>
      <UpsertAgentModal
        agent={agent}
        open={isEditModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
};

const AgentNavSidebar: React.FC<{ isReadOnly: boolean }> = ({ isReadOnly }) => {
  const { agentId, orgId, wsId } = useParamsDecode<AgentVersionsPageParams>();
  const [collapsed, setCollapsed] = useLocalStorage(
    "agentDetailNavigationCollapsed",
    false,
  );

  const currentPageUrl = useLocation();

  return (
    <div className="border-gray-100 border-l">
      <NavSidebar collapsed={collapsed} setCollapsed={setCollapsed}>
        <NavItem
          dataLoc="versions"
          icon={faLayerPlus}
          title="Versions"
          to={getUrlToAgentVersionsPage(orgId, wsId, agentId)}
        />
        <NavItem
          dataLoc="agent-history"
          icon={faWavePulse}
          title="Agent run history"
          to={getUrlToAgentHistoryPage(orgId, wsId, agentId)}
        />
        {!isReadOnly && (
          <NavItem
            dataLoc="agent-settings"
            icon={faCog}
            title="Settings"
            to={appendCurrentFlags(currentPageUrl.pathname) + HashKeys.Edit}
            neverShowActive
          />
        )}
      </NavSidebar>
    </div>
  );
};
